<template>
    <SwipeModal
        v-model="show"
        :class="{ 'is-dragging': isDraggingFromChip }"
        :no-tip="false"
        role="dialog"
        aria-modal="true"
        ref="swipeModal"
        @close="onSwipeModalClose"
    >
        <!--  @touchstart="handleTouchStart"
  @touchmove="handleTouchMove"
  @touchend="handleTouchEnd"
  @mousedown="handleMouseStart"
  @mousemove="handleMouseMove"
  @mouseup="handleMouseEnd" -->
        <div class="draggable-popup-content" ref="content" @mousemove.stop @touchmove.stop>
            <div class="title" v-if="title || $slots.title">
                <slot name="title">
                    {{ title }}
                </slot>
            </div>
            <slot />
            <div class="action-btn" v-if="showActionBtn">
                <van-button type="default" round block @click="cancel">{{ $t('cancel') }}</van-button>
                <van-button type="primary" class="primary-button" round block @click="confirm">
                    {{ $t('confirm') }}
                </van-button>
            </div>
        </div>
    </SwipeModal>
</template>

<script>
import SwipeModal from '@takuma-ru/vue-swipe-modal'

export default {
    name: 'DraggablePopup',
    components: { SwipeModal },
    props: {
        value: { type: Boolean, default: false },
        dragAnywhere: { type: Boolean, default: false },
        showActionBtn: { type: Boolean, default: false },
        title: { type: String, default: '' },
    },
    data() {
        return {
            show: false,
            isDraggingFromChip: false,
            // touch start position (用于判断方向/内部滚动边界)
            // eslint-disable-next-line vue/no-reserved-keys
            _touchStartY: null,
            // eslint-disable-next-line vue/no-reserved-keys
            _touchStartX: null,
            // 被绑定的 handler 引用，方便移除
            // eslint-disable-next-line vue/no-reserved-keys
            _bound: {},
            supportsPassive: false,
        }
    },

    methods: {
        cancel() {
            if (this.$refs.swipeModal && this.$refs.swipeModal.close) {
                this.$refs.swipeModal.close()
            } else {
                this.show = false
            }
            this.$emit('cancel')
        },

        onSwipeModalClose() {
            this.show = false
            // 在弹窗关闭时重置页面滚动
            document.documentElement.style.overflowY = ''
            document.body.style.overflowY = ''
        },

        confirm() {
            this.$emit('confirm')
        },

        detectPassiveSupport() {
            try {
                const opts = Object.defineProperty({}, 'passive', {
                    get: () => {
                        this.supportsPassive = true
                        return true
                    },
                })
                window.addEventListener('testPassive', null, opts)
                window.removeEventListener('testPassive', null, opts)
            } catch (e) {
                this.supportsPassive = false
            }
        },

        // ---------- Vant 风格：overlay 阻止背景滚动的实现 ----------
        // touchstart 记录起点，用于判断滑动方向
        onOverlayTouchStart(e) {
            if (!e.touches || !e.touches[0]) return
            this._touchStartY = e.touches[0].clientY
            this._touchStartX = e.touches[0].clientX
        },

        // touchmove：若触点不在 popup 内容可滚动区域，阻止默认（从而阻止背景滚动）
        onOverlayTouchMove(e) {
            // 如果没有 touches 或被禁止 cancelable，直接 return
            if (!e.touches || !e.touches[0]) return

            const touchCurrentY = e.touches[0].clientY
            const touchCurrentX = e.touches[0].clientX
            const deltaY = touchCurrentY - (this._touchStartY || 0)
            const deltaX = Math.abs(touchCurrentX - (this._touchStartX || 0))

            // 优先允许在弹窗内部具有滚动能力的元素滚动
            const content = this.$refs.content
            if (content && (content === e.target || content.contains(e.target))) {
                // 如果内容可垂直滚动，则判断是否处于边界（若处于边界并且继续往外滑则阻止）
                // 简单判断：检查 content 是否有溢出可滚动
                const canScroll = content.scrollHeight > content.clientHeight

                if (canScroll) {
                    // 找到最接近触点的可滚动元素（防止内部子滚动容器）
                    let el = e.target
                    let allow = false
                    while (el && el !== content && el !== document.body) {
                        const style = window.getComputedStyle(el)
                        const overflowY = style.overflowY
                        const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight
                        if (isScrollable) {
                            // 判断该元素是否在当前滚动方向还有滚动空间
                            if (deltaY > 0 && el.scrollTop > 0) {
                                allow = true
                                break
                            }
                            if (deltaY < 0 && el.scrollTop + el.clientHeight < el.scrollHeight) {
                                allow = true
                                break
                            }
                        }
                        el = el.parentElement
                    }
                    // 如果没找到子滚动器，但 content 本身可滚动，判断 content 边界
                    if (!allow && el === content) {
                        if (deltaY > 0 && content.scrollTop > 0) allow = true
                        if (deltaY < 0 && content.scrollTop + content.clientHeight < content.scrollHeight) allow = true
                    }

                    if (allow) {
                        // 允许内部滚动：不要阻止默认
                        return
                    }
                } else {
                    // 内容不可滚动（高度不足）则阻止背景滚动
                    e.preventDefault && e.preventDefault()
                    return
                }
            } else {
                // 触点不在内容区域，阻止默认（阻止背景滚动）
                e.preventDefault && e.preventDefault()
                return
            }

            // 为保险：若无法判断，则阻止默认（更接近 Vant 的行为）
            e.preventDefault && e.preventDefault()
        },

        // 绑定 overlay style 的事件（打开时）
        attachOverlayHandlers() {
            const opts = this.supportsPassive ? { passive: false } : false
            if (!this._bound) this._bound = {}
            if (!this._bound.overlayStart) {
                this._bound.overlayStart = this.onOverlayTouchStart.bind(this)
                document.addEventListener('touchstart', this._bound.overlayStart, opts)
            }
            if (!this._bound.overlayMove) {
                this._bound.overlayMove = this.onOverlayTouchMove.bind(this)
                document.addEventListener('touchmove', this._bound.overlayMove, opts)
            }
        },

        detachOverlayHandlers() {
            const opts = this.supportsPassive ? { passive: false } : false
            if (this._bound?.overlayMove) {
                document.removeEventListener('touchmove', this._bound.overlayMove, opts)
                this._bound.overlayMove = null
            }
            if (this._bound?.overlayStart) {
                document.removeEventListener('touchstart', this._bound.overlayStart, opts)
                this._bound.overlayStart = null
            }
            this._touchStartY = null
            this._touchStartX = null
        },

        // 下面保留你原有的拖拽相关方法（未改动逻辑，只保留名字）
        handleTouchStart(event) {
            if (this.dragAnywhere) return
            const chip = event.target && event.target.closest && event.target.closest('.modal-contents-chip-wrapper')
            const title = event.target && event.target.closest && event.target.closest('.title')
            const actionBtn = event.target && event.target.closest && event.target.closest('.action-btn')

            if (chip || title || actionBtn) {
                this.isDraggingFromChip = true
                this.setContentTouchAction('none')
            } else {
                this.isDraggingFromChip = false
                event.stopPropagation && event.stopPropagation()
            }
        },

        handleTouchMove(event) {
            if (this.dragAnywhere) return
            if (!this.isDraggingFromChip) {
                event.stopPropagation && event.stopPropagation()
            } else {
                event.stopPropagation && event.stopPropagation()
            }
        },

        handleTouchEnd(event) {
            if (this.dragAnywhere) return
            if (!this.isDraggingFromChip) {
                event.stopPropagation && event.stopPropagation()
            }
            this.setContentTouchAction('')
            this.isDraggingFromChip = false
        },

        handleMouseStart(e) {
            if (this.dragAnywhere) return
            const chip = e.target && e.target.closest && e.target.closest('.modal-contents-chip-wrapper')
            const title = e.target && e.target.closest && e.target.closest('.title')
            const actionBtn = e.target && e.target.closest && e.target.closest('.action-btn')

            if (chip || title || actionBtn) {
                this.isDraggingFromChip = true
                this.setContentTouchAction('none')
            } else {
                this.isDraggingFromChip = false
            }
        },
        handleMouseMove(e) {
            if (this.dragAnywhere) return
            if (!this.isDraggingFromChip) {
                e.stopPropagation && e.stopPropagation()
            }
        },
        handleMouseEnd(e) {
            if (this.dragAnywhere) return
            this.setContentTouchAction('')
            this.isDraggingFromChip = false
        },

        setContentTouchAction(val) {
            const el = this.$refs.content
            if (!el) return
            el.style.touchAction = val
            el.style.webkitTouchAction = val
        },
    },

    watch: {
        value: {
            handler(newVal) {
                this.show = newVal
            },
            immediate: true,
        },
        show: {
            handler(newVal) {
                this.$emit('input', newVal)
                if (newVal) {
                    // 打开：挂载 overlay touch 事件（模拟 Vant overlay 的阻止行为）
                    this.attachOverlayHandlers()
                    // 在弹窗打开时禁止页面滚动
                    document.documentElement.style.overflow = 'hidden'
                    document.body.style.overflow = 'hidden'
                } else {
                    // 关闭：移除
                    this.detachOverlayHandlers()
                    // 在弹窗关闭时重置页面滚动
                    document.documentElement.style.overflow = ''
                    document.body.style.overflow = ''
                }
            },
        },
    },

    mounted() {
        this.detectPassiveSupport()
    },

    beforeDestroy() {
        this.detachOverlayHandlers()
        // 组件销毁时重置页面滚动
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
    },

    destroyed() {
        this.detachOverlayHandlers()
        // 组件销毁时重置页面滚动
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
    },
}
</script>

<style lang="less">
@import url('~@/assets/css/var.less');
.dark .modal-background {
    background: #000 !important;
    opacity: .65;
}
</style>
<style scoped lang="less">
.swipe-modal-takumaru-vue-swipe-modal {
    z-index: 2000 !important;
}
.title {
    padding: 0 16px;
    font-size: var(--fs-font-size-20);
    color: var(--fs-primary);
    line-height: 26px;
    padding-bottom: 20px;
    font-weight: 600;
    position: sticky;
    top: calc(var(--chip-wrapper-height) - 2px);
    background: var(--fs-white);
}
.action-btn {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    padding: 16px;
    position: sticky;
    bottom: 0;
    background: var(--fs-white);
    .van-button--primary {
        background-color: var(--fs-primary) !important;
        border: none;
        color: var(--fs-white);
        font-size: var(--fs-font-size-16);
        line-height: 22px;
        font-weight: 400;
        width: 100% !important;
    }
}
:deep(.modal-contents) {
    --chip-wrapper-height: 28px;
    --chip-wrapper-margin-bottom: 12px;
    border-radius: 12px 12px 0 0 !important;
    background-color: var(--fs-white) !important;
    color: unset !important;
    max-height: 90vh !important;
    overscroll-behavior: none;
    min-height: 20vh !important;
    max-width: 540px;
}


/* 可拖拽条的样式 */
:deep(.modal-contents-chip-wrapper) {
    display: flex;
    justify-content: center;
    padding: 7px 0;
    height: var(--chip-wrapper-height);
    padding-bottom: calc(var(--chip-wrapper-margin-bottom) + 7px) !important;
    position: sticky !important;
    top: 0;
    background: inherit;
}

:deep(.modal-contents-chip) {
    width: 36px !important;
    height: 3px !important;
    background-color: #ddd !important;
    border-radius: 4px !important;
}

/* 拖拽期间给一个视觉反馈（可选） */
.draggable-popup-content.is-dragging {
    cursor: grabbing;
    /* 在拖拽时禁用子元素的 pointer events（按需开启） */
    /* pointer-events: none; */
}

/* 内容区基础样式，确保内部可以滚动（若需要） */
.draggable-popup-content {
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y;
    /* 默认允许纵向滚动 */
}
</style>
